import { formatEur, type MemberDto } from "@gladis/shared";
import { firstNameOf, StatCard, StatRow } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { formatDay } from "./format-day.js";
import { useSavingsAccounts } from "./AccountList.js";

/**
 * The row of figures the Savings page opens with.
 *
 * ==========================================================================================
 * THREE CARDS, AND THEY USED TO BE A HEADER
 * ==========================================================================================
 *
 * The total and the joint/per-member breakdown were `AccountList`'s header, which put the household's
 * headline figure inside the component that lists accounts — so it appeared below whichever form happened to be
 * open, and a page with no accounts had no total at all. They are the page's figures, not the list's.
 *
 * The third is new and is the one the total cannot answer: **whether the household saved or spent this month.**
 * A single balance says what is there; the movement says which way it is going, which is the question somebody
 * opening this page every month is actually asking.
 *
 * ==========================================================================================
 * IT FETCHES NOTHING OF ITS OWN
 * ==========================================================================================
 *
 * `useSavingsAccounts` is `AccountList`'s hook and its cache key, so the list below and these cards are one
 * request and one answer. Two reads of the same endpoint could return figures either side of a balance being
 * recorded, and the total would then disagree with the rows under it.
 *
 * NOTHING HERE IS COMPUTED. The total, every breakdown row and the month's change all arrive summed and
 * subtracted in integer cents by the server. `sinceLastMonth.change` in particular is deliberately not
 * `total - sinceLastMonth.total` done here: that is float arithmetic on money, and it produces
 * €770,2200000000012 on the first household whose balances are not round numbers.
 */
export function SavingsStats({
  members = [],
}: {
  /** The household's members, for their faces (020). */
  members?: readonly MemberDto[];
} = {}): ReactNode {
  const t = useT();
  // Above the early return below, with the other hook: hooks must run in the same order on every
  // render, and this component returns null before it has data.
  const accounts = useSavingsAccounts();

  /*
   * NOTHING UNTIL THERE IS SOMETHING. No skeleton and no zeroes: the list below says "Loading your savings…"
   * once, and a row of placeholder cards above it would be a second, louder claim about the same wait. A
   * household with no accounts gets the list's empty state rather than three cards reading € 0,00, which is a
   * figure they would have to interpret.
   */
  if (!accounts.data || accounts.data.active.length === 0) return null;

  const { total, breakdown, sinceLastMonth, active } = accounts.data;
  const change = sinceLastMonth?.change ?? 0;

  return (
    <StatRow>
      <StatCard
        label={t("savings.stats.total")}
        value={formatEur(total)}
        // The testid the e2e suite has always used for this figure, carried across from `AccountList`'s header
        // so the assertion follows the number rather than the markup that happened to hold it. On the FIGURE,
        // not the card: the suite asserts `toHaveText("€ 6.834,56")`, which a card holding a label and a hint
        // could never satisfy.
        valueTestId="savings-total"
        hint={
          <>
            Across {active.length} {active.length === 1 ? "account" : "accounts"}.
          </>
        }
      />

      {sinceLastMonth === null ? null : (
        <StatCard
          label={t("savings.stats.changeThisMonth")}
          // Signed always, including zero — an unchanged month is a finding, and an unsigned figure would make
          // €770 saved and €770 spent look identical at a glance.
          value={`${change > 0 ? "+" : change < 0 ? "−" : ""} ${formatEur(Math.abs(change))}`}
          trend={change > 0 ? "up" : change < 0 ? "down" : "level"}
          testId="savings-change"
          hint={
            <>
              {formatEur(sinceLastMonth.total)} on {formatDay(sinceLastMonth.asOf)}.
            </>
          }
        />
      )}

      {/*
        ONE HOLDER MEANS NO CARD, which is the rule the header this replaced followed: a household whose savings
        are all in joint accounts would read a breakdown of one row equal to the total beside it.
      */}
      {breakdown.length > 1 ? (
        <StatCard
          label={t("savings.stats.whoHolds")}
          testId="savings-breakdown"
          rows={breakdown.map((row) => ({
            key: row.key,
            /*
              THE STAND-INS ARE CHOSEN HERE, not in the use case that used to compose them. A null
              label means the row is not about a named person: `key === "joint"` is the household's
              own money, and anything else is an owner who left without a snapshot.
            */
            label:
              row.label === null
                ? t(row.key === "joint" ? "person.joint" : "person.formerMember")
                : firstNameOf(row.label),
            value: formatEur(row.total),
            // `key` is "joint" or a user id, so the lookup answers both questions at once: a joint
            // row matches nobody and gets no mark, which is what it should have.
            /*
              THREE CASES, AND THE THIRD IS 020's FR-024 RATHER THAN AN OVERSIGHT.

              Joint is the household, so it wears everybody. A named holder wears their own face, or
              their initials. A row whose label is null is about somebody the household can no longer
              identify — a departed owner whose account snapshotted no name — and it gets NO MARK,
              exactly like Joint. 020 settled this: initials for a stand-in render cleanly, read as
              data and fail no test, which is why "A former member" must not become "AM".
            */
            ...(row.key === "joint"
              ? {
                  avatarGroup: members.map((m) => ({
                    name: m.displayName,
                    avatarUrl: m.avatarUrl,
                  })),
                }
              : row.label === null
                ? {}
                : {
                    // Full name for the initials; the label above is the first name only.
                    avatarName: row.label,
                    avatarUrl: members.find((m) => m.userId === row.key)?.avatarUrl ?? null,
                  }),
          }))}
          hint={
            <>
              These sum to the total, exactly. A joint account is its own row rather than divided
              between members.
            </>
          }
        />
      ) : null}
    </StatRow>
  );
}
